import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { useAuth } from "@/hooks/useAuth";
import { Sidebar } from "@/components/dashboard/Sidebar";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { ClientReferralModal } from "@/components/referrals/ClientReferralModal";
import { RewardCard, CTASection, ProcessInfo, ClientReferralsList } from "@/components/client-referral";
import { ClientReferral as ClientReferralType } from "@/lib/schema";
import { getClientReferralBonusAmount } from "@/api/referralSettings";
import { getClientReferrals } from "@/api/clientReferrals";

export default function ClientReferral() {
  const { userData } = useAuth();
  const [clientReferralModalOpen, setClientReferralModalOpen] = useState(false);

  // Get client referral bonus amount from settings
  const { data: clientReferralBonus = 20000 } = useQuery<number>({
    queryKey: ["referralSettings", "clientReferralBonus"],
    queryFn: getClientReferralBonusAmount,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  const { data: clientReferrals = [], isLoading } = useQuery<ClientReferralType[]>({
    queryKey: ["clientReferrals", userData?.id],
    queryFn: () => getClientReferrals(userData!.id),
    enabled: !!userData?.id,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  const displayReferrals = clientReferrals;

  const handleReferClient = () => {
    setClientReferralModalOpen(true);
  };

  return (
    <ProtectedRoute>
      <div className="min-h-screen page-gradient">
        <Sidebar />
        <div className="ml-0 lg:ml-56 p-3 sm:p-4 lg:p-8 pt-16 sm:pt-20 lg:pt-8">
          {/* Dashboard Header */}
          <DashboardHeader 
            title="Client Referral"
            subtitle="Refer companies to our platform and earn rewards for successful partnerships."
            className="mb-6 lg:mb-8"
          />

          {/* Mobile-First CTA Section */}
          <div className="mb-4 sm:mb-6 lg:hidden">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <RewardCard rewardAmount={clientReferralBonus} isMobile={true} />
              <CTASection onReferClient={handleReferClient} isMobile={true} />
            </div>
          </div>

          {/* Process Information - Mobile Optimized */}
          <div className="mb-4 sm:mb-6 lg:hidden">
            <ProcessInfo isMobile={true} />
          </div>


          {/* Desktop Layout - Hidden on Mobile */}
          <div className="hidden lg:grid lg:grid-cols-5 gap-4 lg:gap-8 mb-6">
            {/* Left Content - Desktop Process Information */}
            <div className="col-span-3 order-2 lg:order-1">
              <ProcessInfo isMobile={false} />
            </div>

            {/* Right Sidebar - Desktop CTA */}
            <div className="col-span-2 order-1 lg:order-2">
              <div className="h-full flex flex-col space-y-4 lg:space-y-6">
                <RewardCard rewardAmount={clientReferralBonus} className="flex-1" />
                <CTASection onReferClient={handleReferClient} />
              </div>
            </div>
          </div>

          {/* Client Referrals List */}
          <ClientReferralsList 
            referrals={displayReferrals}
            onReferClient={handleReferClient}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Client Referral Modal */}
      <ClientReferralModal
        open={clientReferralModalOpen}
        onOpenChange={setClientReferralModalOpen}
        rewardAmount={clientReferralBonus}
      />
    </ProtectedRoute>
  );
}
